<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb name="Dashboard"/>
    <b-row>
      <b-col md="6">
        <admin-panel
            title="Recent Applications"
            sub-title="Companies applications that have not been verified yet"
        >
          <template v-slot:header-right>
           <div>
             <b-btn
                 v-if="pending_applications.rows.length > 0"
                 variant="primary"
                 :to="{name: 'admin-applications'}"
             >
               View All
             </b-btn>
           </div>
          </template>
          <b-overlay :show="pending_applications.loading">
            <admin-applications-table :applications="pending_applications.rows" small/>
          </b-overlay>
        </admin-panel>
      </b-col>
      <b-col md="6">
        <admin-panel
            title="Last Approved Companies"
        >
          <b-overlay :show="last_approved_companies.loading">
            <admin-companies-table :companies="last_approved_companies.rows" small/>
          </b-overlay>
        </admin-panel>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <admin-panel
            title="Licenses Awaiting Approval"
            sub-title="Licenses that have not been confirmed"
        >
          <b-overlay :show="not_approved_licenses.loading">
            <admin-licenses-table
                :licenses="not_approved_licenses.rows"
                @approve="requestApproveLicense"
                with-actions
                with-company
            />
          </b-overlay>
        </admin-panel>
        <admin-license-approve-modal
            v-if="license_to_approve"
            :license="license_to_approve"
            @approve="approveLicense"
            @cancel="license_to_approve = null"
            visible
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col lg="8" md="7">
        <admin-panel
          title="Last Registered Users"
        >
          <b-overlay :show="last_registered_users.loading">
            <admin-users-table :users="last_registered_users.rows" no-avatar small/>
          </b-overlay>
        </admin-panel>
      </b-col>
      <b-col lg="4" md="5">
        <admin-panel
          title="Current Stats"
          sub-title="General GreenLists Stats"
        >
          <h5 v-if="total_companies !== null">Total Companies: <strong>{{ total_companies }}</strong></h5>
          <h5 v-if="active_companies !== null">Active Companies: <strong>{{ active_companies }}</strong></h5>
          <h5 v-if="total_users !== null">Total Users: <strong>{{ total_users }}</strong></h5>
          <h5 v-if="active_users !== null">Active Users: <strong>{{ active_users }}</strong></h5>
          <h5 v-if="total_licenses !== null">Total Facilities: <strong>{{ total_licenses }}</strong></h5>
          <h5 v-if="active_licenses !== null">Active Facilities: <strong>{{ active_licenses }}</strong></h5>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import AdminPanel from "../components/AdminPanel";
import AdminUsersTable from "../components/AdminUsersTable";
import {API_ROUTES} from "../api";
import AdminCompaniesTable from "../components/AdminCompaniesTable";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminLicensesTable from "../components/AdminLicensesTable";
import AdminLicenseApproveModal from "../components/AdminLicenseApproveModal";
import AdminPreloader from "../components/AdminPreloader";
import AdminApplicationsTable from "../components/AdminApplicationsTable";
export default {
  name: "AdminDashboard",
  components: {
    AdminApplicationsTable,
    AdminPreloader,
    AdminLicenseApproveModal,
    AdminLicensesTable,
    AdminBreadcrumb,
    AdminCompaniesTable,
    AdminUsersTable,
    AdminPanel
  },
  data() {
    return {
      loading: false,
      last_registered_users: {
        loading: true,
        rows: []
      },
      pending_applications: {
        loading: true,
        rows: []
      },
      last_approved_companies: {
        loading: true,
        rows: []
      },
      not_approved_licenses: {
        loading: true,
        rows: []
      },
      total_users: null,
      active_users: null,
      total_companies: null,
      active_companies: null,
      total_licenses: null,
      active_licenses: null,
      license_to_approve: null
    }
  },
  created() {
    this.loadLastRegisteredUsers();
    this.loadLastApprovedCompanies();
    this.loadPendingApplications();
    this.loadNotApprovedLicenses();
    this.loadTotalUsersNumber();
    this.loadTotalCompaniesNumber();
    this.loadTotalLicensesNumber();
  },
  methods: {
    loadLastRegisteredUsers() {
      this.$api.get(API_ROUTES.ADMIN_USERS, {
        params: {
          sorting: 'last_created',
          per_page: 5,
          page: 1
        }
      }).then(({data}) => {
        this.last_registered_users.rows = data.users;
      }).finally(() => {
        this.last_registered_users.loading = false;
      });
    },
    loadLastApprovedCompanies() {
      this.$api.get(API_ROUTES.ADMIN_COMPANIES, {
        params: {
          status: 'active',
          sorting: 'last_approved',
          per_page: 5,
          page: 1
        }
      }).then(({data}) => {
        this.last_approved_companies.rows = data.companies;
      }).finally(() => {
        this.last_approved_companies.loading = false;
      });
    },
    loadPendingApplications() {
      this.$api.get(API_ROUTES.ADMIN_APPLICATIONS, {
        params: {
          per_page: 5,
          page: 1
        }
      }).then(({data}) => {
        this.pending_applications.rows = data.applications;
      }).finally(() => {
        this.pending_applications.loading = false;
      });
    },
    loadNotApprovedLicenses() {
      this.$api.get(API_ROUTES.ADMIN_LICENSES, {
        params: {
          is_approved: 0,
          hide_inactive_companies: 1
        }
      }).then(({data}) => {
        this.not_approved_licenses.rows = data.licenses;
      }).finally(() => {
        this.not_approved_licenses.loading = false;
      });
    },
    loadTotalUsersNumber() {
      this.$api.get(API_ROUTES.ADMIN_USERS).then(({data}) => {
        this.total_users = data.users.length;
        this.active_users = data.users.filter(user => user.is_active).length;
      });
    },
    loadTotalCompaniesNumber() {
      this.$api.get(API_ROUTES.ADMIN_COMPANIES).then(({data}) => {
        this.total_companies = data.companies.length;
        this.active_companies = data.companies.filter(company => company.status?.code === 'active').length;
      });
    },
    loadTotalLicensesNumber() {
      this.$api.get(API_ROUTES.ADMIN_LICENSES).then(({data}) => {
        this.total_licenses = data.licenses.length;
        this.active_licenses = data.licenses.filter(license => !!license.is_approved).length;
      });
    },
    requestApproveLicense(license) {
      this.license_to_approve = license;
    },
    approveLicense(payload) {
      let license = payload.license,
          formData = {
            is_approved: true,
            is_to_buy: payload.is_to_buy,
            is_to_sell: payload.is_to_sell
          };
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_LICENSES}/${license.id}`, formData).then(() => {
        let licenseIndex = this.not_approved_licenses.rows.findIndex(l => l.id === license.id),
            licenses = [...this.not_approved_licenses.rows];
        licenses.splice(licenseIndex, 1);
        this.not_approved_licenses.rows = licenses;
        this.$bvToast.toast('License has been successfully approved', {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    }
  }
}
</script>
